import { Injectable } from '@angular/core';
import { NFC } from '@awesome-cordova-plugins/nfc/ngx';
import { Platform } from '@ionic/angular';
import {
  BehaviorSubject,
  catchError,
  distinctUntilChanged,
  merge,
  NEVER,
  Observable,
  Subject,
  Subscriber,
  Subscription,
  switchMap,
  take,
  throwError,
} from 'rxjs';
import { NdefRecord, NfcTag } from '@awesome-cordova-plugins/nfc';
import { Nullable } from 'src/core/common/domain/types/types';
import { Initializable } from 'src/core/common/domain/initializer/initializable';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import { NdefTextRecord } from './ndef-text-record';
import { NfcWriteErrorParser } from './nfc-write-error-parser';

export type NFCResponse = { data: string; serialNumber: string; hasUnrecognizedContent: boolean };

@Injectable({
  providedIn: 'root',
})
export class NFCService implements Initializable {
  private static readonly TNF_EMPTY = 0x00;
  // What the plugin rejects any call with while the device has no NFC or has it turned off.
  private static readonly NFC_UNAVAILABLE_STATUSES = ['NFC_DISABLED', 'NO_NFC'];

  private isEnabled = false;
  private nfcDataSubject: Subject<NFCResponse> = new Subject<NFCResponse>();
  private readySubject = new BehaviorSubject<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/member-ordering
  ready$: Observable<boolean> = this.readySubject.asObservable();
  private nfcSubscription: Subscription;
  private readerModeSubscription: Nullable<Subscription> = null;
  private readerModeRequested = false;
  private writingOnTag = false;
  private readingTagEnabled = true;
  private readonly readingTagSemaphoreWindowTime = 1_500;
  private exclusiveReaders: Array<Subscriber<NFCResponse>> = [];

  constructor(private platform: Platform, private nfc: NFC, private diagnostic: Diagnostic) {
    // Fire-and-forget, and the failure is discarded on purpose: if the rejection bubbled up,
    // CustomErrorHandler would show an error toast with no context for the user.
    // It is logged to the console so it is not lost entirely when debugging.
    this.init().catch(console.error);
  }

  listen(): Observable<NFCResponse> {
    if (!this.platform.is('cordova')) {
      return new Observable();
    }

    this.readerModeRequested = true;
    this.enableReaderModeIfPossible();

    return this.nfcDataSubject.asObservable();
  }

  // While a screen reads exclusively, tags are not handed to the rest of the app, so inspecting a tag
  // cannot also change the selected location or log the user out. Unsubscribing gives the reads back.
  // Exclusive readers stack up: the attendance tab keeps reading while a modal opened on top of it reads
  // too, and gets the tags back once that modal closes.
  readExclusively(): Observable<NFCResponse> {
    return new Observable<NFCResponse>((subscriber) => {
      this.exclusiveReaders.push(subscriber);

      return () => {
        this.exclusiveReaders = this.exclusiveReaders.filter((reader) => reader !== subscriber);
      };
    });
  }

  // The plugin can only write on a tag delivered by a foreground dispatch, never on one delivered in reader mode.
  // So reader mode is paused while waiting for the tag, and resumed once the write is over or abandoned.
  writeOnNextTag(record: NdefTextRecord): Observable<void> {
    if (!this.platform.is('cordova')) {
      return new Observable();
    }

    return new Observable<void>((subscriber) => {
      this.writingOnTag = true;
      this.disableReaderMode();

      const nextTag = this.ready$
        .pipe(
          distinctUntilChanged(),
          // The listeners are dropped while NFC is off and registered again once it is back on,
          // which makes the plugin restart the foreground dispatch they rely on.
          switchMap((enabled) => (enabled ? this.tagsToWriteOn() : NEVER)),
          take(1)
        )
        .subscribe({
          next: () => {
            this.nfc.write([record.toPrimitives()]).then(
              () => {
                subscriber.next();
                subscriber.complete();
              },
              (error) => subscriber.error(NfcWriteErrorParser.toException(error))
            );
          },
          error: (error) => subscriber.error(NfcWriteErrorParser.toException(error)),
        });

      return () => {
        nextTag.unsubscribe();
        this.writingOnTag = false;
        this.enableReaderModeIfPossible();
      };
    });
  }

  private tagsToWriteOn(): Observable<unknown> {
    return merge(this.nfc.addNdefListener(), this.nfc.addTagDiscoveredListener()).pipe(
      // NFC turned off right as the listeners register: keep waiting until it is turned on again.
      catchError((error) => (NFCService.NFC_UNAVAILABLE_STATUSES.includes(error) ? NEVER : throwError(() => error)))
    );
  }

  private enableReaderModeIfPossible() {
    // Reader mode is off until the app listens, cannot be on while NFC is off,
    // and would take the tag away from the listeners a write is waiting on.
    if (!this.readerModeRequested || !this.isEnabled || this.writingOnTag) {
      return;
    }

    const flags = this.getReaderFlags();

    // Read NFC Tag - Android
    // Once the reader mode is enabled, any tags that are scanned are sent to the subscriber
    this.readerModeSubscription = this.nfc.readerMode(flags).subscribe({
      next: (tag: NfcTag) => {
        if (!this.readingTagEnabled) {
          return;
        }

        this.readingTagEnabled = false;
        setTimeout(() => {
          this.readingTagEnabled = true;
        }, this.readingTagSemaphoreWindowTime);

        this.dispatch(this.responseFor(tag));
      },
      error: (error) => {
        // Erroring the shared subject would cut the app off NFC reads for good. The next listen() enables it again.
        console.error('[NFCService] Reader mode failed', error);
        this.readerModeSubscription = null;
      },
    });
  }

  private disableReaderMode() {
    if (this.readerModeSubscription === null) {
      return;
    }

    // Unsubscribing is what makes the plugin disable reader mode.
    this.readerModeSubscription.unsubscribe();
    this.readerModeSubscription = null;
  }

  private responseFor(tag: NfcTag): NFCResponse {
    const hexSerialNumber = this.getHexadecimalSerialNumber(tag);
    let encodedString = '';
    if (tag && tag.ndefMessage) {
      encodedString = this.decodeNdefMessage(tag.ndefMessage);
    }

    const response = {
      data: encodedString,
      serialNumber: hexSerialNumber,
      hasUnrecognizedContent: this.hasUnrecognizedContent(tag),
    };
    return response;
  }

  // Only the first record is decoded, and only a Text record decodes into readable text.
  private hasUnrecognizedContent(tag: NfcTag): boolean {
    const firstRecord = tag?.ndefMessage?.[0];
    if (!firstRecord || firstRecord.tnf === NFCService.TNF_EMPTY) {
      return false;
    }

    return !NdefTextRecord.isTextRecord(firstRecord);
  }

  private dispatch(response: NFCResponse) {
    const exclusiveReader = this.exclusiveReaders[this.exclusiveReaders.length - 1];
    if (exclusiveReader) {
      exclusiveReader.next(response);
      return;
    }

    this.nfcDataSubject.next(response);
  }

  private registerNFCStateChange() {
    this.diagnostic.registerNFCStateChangeHandler((state: string) => {
      switch (state) {
        case this.diagnostic.NFCState.UNKNOWN:
        case this.diagnostic.NFCState.POWERED_OFF:
          this.disable();
          break;
        case this.diagnostic.NFCState.POWERED_ON:
          this.enable();
          break;
        case this.diagnostic.NFCState.POWERING_OFF:
        case this.diagnostic.NFCState.POWERING_ON:
          break;
      }
    });
  }

  private getHexadecimalSerialNumber(tag: NfcTag) {
    return tag.id
      .map((char: number) => this.decimalToHexLittleEndian(char))
      .join(':')
      .toUpperCase();
  }

  private decimalToHexLittleEndian(decimalNumber: number): string {
    // eslint-disable-next-line no-bitwise
    return (decimalNumber & 0xff).toString(16).padStart(2, '0');
  }

  private getReaderFlags(): number {
    // eslint-disable-next-line no-bitwise
    return this.nfc.FLAG_READER_NFC_A | this.nfc.FLAG_READER_NFC_V;
  }

  private async init() {
    await this.platform.ready();
    if (!this.platform.is('cordova')) {
      return;
    }

    this.registerNFCStateChange();

    // first time
    const isAvailable = await this.diagnostic.isNFCAvailable();
    if (isAvailable) {
      this.enable();
    } else {
      this.disable();
    }
  }

  private enable() {
    this.isEnabled = true;
    this.readySubject.next(this.isEnabled);
  }

  private disable() {
    this.isEnabled = false;
    this.readySubject.next(this.isEnabled);
  }

  private decodeNdefMessage(ndefMessage: NdefRecord[]): Nullable<string> {
    const storedStrings = ndefMessage.map((n: NdefRecord) => {
      const payload = n.payload;
      if (!payload || payload.length === 0) {
        return '';
      }

      const statusByte = payload[0];
      // eslint-disable-next-line no-bitwise
      const langCodeLength = statusByte & 0x3f; // 0x3f = 00111111 Set the first 2 bits to 0.
      const textBytes = payload.slice(1 + langCodeLength);

      return this.nfc.bytesToString(textBytes);
    });

    return storedStrings.length > 0 ? storedStrings[0] : null;
  }

  private unsubscribeFromNFC() {
    if (this.nfcSubscription) {
      this.nfcSubscription.unsubscribe();
    }
  }
}
